import React, { createContext, useContext, useMemo } from 'react';
import type { CSSProperties } from 'react';
import type {
  DraggableSyntheticListeners,
  UniqueIdentifier,
} from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, SxProps, Theme } from '@mui/material';

interface SortableItemProps {
  children?: React.ReactNode;
  id: UniqueIdentifier;
  disabled?: boolean;
}

interface SortableItemContextValue {
  attributes: Record<string, any>;
  listeners: DraggableSyntheticListeners;
  ref(node: HTMLElement | null): void;
  isDragging: boolean;
}

const SortableItemContext = createContext<SortableItemContextValue>({
  attributes: {},
  listeners: undefined,
  ref: () => {},
  isDragging: false,
});

export const SortableItem = ({
  children = null,
  id,
  disabled = false,
}: SortableItemProps) => {
  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
  } = useSortable({ id, disabled });

  const context = useMemo(
    () => ({
      attributes,
      listeners,
      ref: setActivatorNodeRef,
      isDragging,
    }),
    [attributes, listeners, setActivatorNodeRef, isDragging]
  );

  const style: CSSProperties = disabled
    ? {}
    : {
        transform: CSS.Translate.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      };

  return (
    <SortableItemContext.Provider value={context}>
      <div ref={setNodeRef} style={style}>
        {children}
      </div>
    </SortableItemContext.Provider>
  );
};

interface DragHandleProps {
  children?: React.ReactNode;
  sx?: SxProps<Theme>;
}

export const DragHandle = ({ children = null, sx = {} }: DragHandleProps) => {
  const { attributes, listeners, ref, isDragging } =
    useContext(SortableItemContext);

  return (
    <Box
      ref={ref}
      {...attributes} // eslint-disable-line react/jsx-props-no-spreading
      {...listeners} // eslint-disable-line react/jsx-props-no-spreading
      sx={[
        {
          display: 'flex',
          alignItems: 'center',
          cursor: isDragging ? 'grabbing' : 'grab',
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {children}
    </Box>
  );
};
